import React, { useCallback, useState } from 'react';
import {
  Avatar,
  Box,
  Tooltip,
  Popover,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Typography,
  InputAdornment,
  TextField,
  IconButton
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

// Utility to adjust color brightness for hover/focus states
function adjustColor(hex: string): string {
  const hexToRgb = (hex: string) => {
    hex = hex.replace(/^#/, '');
    const bigint = parseInt(hex, 16);
    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255,
    };
  };

  const rgbToHex = (r: number, g: number, b: number): string => {
    const toHex = (n: number) => n.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  const calculateBrightness = ({ r, g, b }: { r: number; g: number; b: number }) =>
    (r * 299 + g * 587 + b * 114) / 1000;

  const adjustBrightness = ({ r, g, b }: { r: number; g: number; b: number }, factor: number) => ({
    r: Math.min(Math.max(0, r + factor), 255),
    g: Math.min(Math.max(0, g + factor), 255),
    b: Math.min(Math.max(0, b + factor), 255),
  });

  const rgb = hexToRgb(hex);
  const brightness = calculateBrightness(rgb);
  const isDark = brightness < 128;
  const adjustmentFactor = isDark ? 40 : -40;
  const adjustedRgb = adjustBrightness(rgb, adjustmentFactor);
  return rgbToHex(adjustedRgb.r, adjustedRgb.g, adjustedRgb.b);
}

interface AvatarListProps {
  avatars: { name: string; src: string; id: number; selected?: boolean; color?: string }[];
  maxWidth: number;
  onClick: (id: number) => void;
}

const HorizontalAvatarList: React.FC<AvatarListProps> = ({ avatars, maxWidth, onClick }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const VISIBLE_COUNT = 6;
  const { t: tGeneral } = useTranslation('general');

  const numberToColorHash = useCallback((num: number) => {
    const seededRandom = (seed: number) => {
      const x = Math.sin(seed) * 10000;
      return x - Math.floor(x);
    };
    const r = Math.floor(seededRandom(num * 1.1) * 128) + 128;
    const g = Math.floor(seededRandom(num * 2.3) * 128) + 128;
    const b = Math.floor(seededRandom(num * 3.7) * 128) + 128;
    const toHex = (n: number) => (n.toString(16).length === 1 ? '0' : '') + n.toString(16);
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }, []);

  const doctorColorMap: Record<number, string> = {
    [-1]: '#8192A4',
    1: '#7EA086',
    2: '#A38296',
    3: '#7CA19C',
    4: '#9A8D78',
    5: '#A8C2BB',
    6: '#9B7A7A',
    7: '#CBAF9C',
    8: '#B0AAC8',
    9: '#8E997C',
    10: '#C8C396',
    11: '#727272',
    12: '#8F7C99',
  };

  const selectedCountRaw = avatars.filter(a => a.selected).length;

  const updatedAvatars = [
    // "All" selected only when no doctors are selected
    { name: tGeneral('all'), src: '', id: -1, selected: selectedCountRaw === 0 },
    ...avatars,
  ].reduce((acc, current) => {
    const x = acc.find(item => item.id === current.id);
    if (!x) {
      const color = doctorColorMap[current.id] || current.color || numberToColorHash(current.id);
      return acc.concat([{ ...current, color }]);
    } else {
      return acc;
    }
  }, [] as { name: string; src: string; id: number; selected?: boolean; color?: string }[]);

  const visibleAvatars = updatedAvatars.slice(0, VISIBLE_COUNT);
  const hiddenAvatars = updatedAvatars.slice(VISIBLE_COUNT);
  const hasMore = updatedAvatars.length > VISIBLE_COUNT;
  const remainingCount = updatedAvatars.length - VISIBLE_COUNT;
  const selectedCount = updatedAvatars.filter(a => a.selected && a.id !== -1).length;

  const filteredHiddenAvatars = hiddenAvatars.filter(avatar =>
    avatar.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handlePopoverOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setSearchQuery('');
  }, []);

  const clearAllSequential = useCallback(() => {
    onClick(-1);
  }, [onClick]);
  const handlePopoverClose = useCallback(() => {
    setAnchorEl(null);
    setSearchQuery('');
  }, []);

  const handleAvatarClick = useCallback(
    (id: number) => {
      if (id === -1) {
        clearAllSequential();
        handlePopoverClose();
        return;
      }
      onClick(id);
    },
    [clearAllSequential, handlePopoverClose, onClick]
  );

  const open = Boolean(anchorEl);

  const getAllTooltip = () => (selectedCount > 0 ? tGeneral('clearAll') : tGeneral('all'));

  return (
    <Box sx={{ display: 'flex', maxWidth: `${maxWidth}px`, mt: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '4px 8px',
          borderRadius: '28px',
          background: '#f8f9fa',
          border: '1px solid #e9ecef',
        }}
      >
        {visibleAvatars.map((avatar) => (
          <Tooltip
            key={avatar.id}
            title={avatar.id === -1 ? getAllTooltip() : avatar.name}
            placement="top"
            arrow
          >
            <Box
              role="button"
              aria-label={avatar.id === -1 ? getAllTooltip() : avatar.name}
              onClick={() => handleAvatarClick(avatar.id)}
              tabIndex={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 12px 4px 4px',
                borderRadius: '20px',
                cursor: 'pointer',
                backgroundColor: avatar.selected ? avatar.color : 'transparent',
                border: avatar.selected ? 'none' : `1.5px solid ${avatar.color}60`,
                transition: 'all 0.18s ease',
                '&:hover, &:focus': {
                  backgroundColor: avatar.selected ? adjustColor(avatar.color || '#ccc') : `${avatar.color}15`,
                  outline: avatar.selected
                    ? '2px solid #5A8AEA'
                    : '1.5px solid #bbbbbb',
                  transform: 'scale(1.03)',
                },
              }}
            >
              <Avatar
                src={avatar.src}
                sx={{
                  width: 24,
                  height: 24,
                  bgcolor: avatar.src ? 'transparent' : (avatar.selected ? 'rgba(255,255,255,0.25)' : avatar.color),
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  color: '#fff', // simplified: same value for both states
                }}
              >
                {!avatar.src && (avatar.id === -1 ? '✓' : avatar.name[0].toUpperCase())}
              </Avatar>
              <Typography
                sx={{
                  fontSize: '0.78rem',
                  fontWeight: avatar.selected ? 600 : 500,
                  color: avatar.selected ? '#fff' : '#495057',
                  whiteSpace: 'nowrap',
                }}
              >
                {avatar.id === -1
                  ? (selectedCount > 0 ? tGeneral('allSelected', { count: selectedCount }) : tGeneral('all'))
                  : (avatar.name.length > 8 ? `${avatar.name.slice(0, 8)}…` : avatar.name)
                }
              </Typography>
            </Box>
          </Tooltip>
        ))}

        {hasMore && (
          <>
            <Box
              role="button"
              aria-label={tGeneral('showMore')}
              tabIndex={0}
              onClick={handlePopoverOpen}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handlePopoverOpen(e as unknown as React.MouseEvent<HTMLElement>);
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '2px',
                padding: '6px 10px',
                borderRadius: '16px',
                cursor: 'pointer',
                border: '1.5px dashed #E17E61',
                backgroundColor: open ? '#fff5f2' : 'transparent',
                transition: 'all 0.15s ease',
                '&:hover, &:focus': {
                  backgroundColor: '#fff5f2',
                  outline: '2px solid #e17e61',
                },
              }}
            >
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#E17E61' }}>
                +{remainingCount}
              </Typography>
              <KeyboardArrowDownIcon
                sx={{
                  fontSize: '1rem',
                  color: '#E17E61',
                  transform: open ? 'rotate(180deg)' : 'rotate(0)',
                  transition: 'transform 0.2s ease',
                }}
              />
            </Box>
            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handlePopoverClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              transformOrigin={{ vertical: 'top', horizontal: 'center' }}
              slotProps={{
                paper: {
                  sx: {
                    mt: 1,
                    borderRadius: '16px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                    border: '1px solid #f0f0f0',
                    width: '240px',
                    overflow: 'hidden',
                  },
                },
              }}
            >
              <Box sx={{ p: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                <TextField
                  size="small"
                  placeholder={tGeneral('searchDoctor')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: '1.1rem', color: '#adb5bd' }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton size="small" aria-label={tGeneral('clearSearch')} onClick={() => setSearchQuery('')}>
                          <CloseIcon sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      fontSize: '0.82rem',
                      backgroundColor: '#f8f9fa',
                      '& fieldset': { border: 'none' },
                    },
                  }}
                />
              </Box>
              <List sx={{ py: 0.5, maxHeight: '220px', overflowY: 'auto' }}>
                {filteredHiddenAvatars.length > 0 ? (
                  filteredHiddenAvatars.map((avatar) => (
                    <ListItemButton
                      key={avatar.id}
                      onClick={() => handleAvatarClick(avatar.id)}
                      aria-label={avatar.name}
                      sx={{
                        py: 1,
                        px: 1.5,
                        mx: 0.75,
                        mb: 0.5,
                        borderRadius: '10px',
                        backgroundColor: avatar.selected ? `${avatar.color}12` : 'transparent',
                        '&:hover, &:focus': { backgroundColor: `${avatar.color}18` },
                        outline: avatar.selected ? '2px solid #5A8AEA' : undefined,
                      }}
                    >
                      <ListItemAvatar sx={{ minWidth: 38 }}>
                        <Avatar
                          src={avatar.src}
                          sx={{
                            width: 30,
                            height: 30,
                            bgcolor: avatar.color,
                            fontSize: '0.72rem',
                            fontWeight: 600,
                            boxShadow: avatar.selected ? `0 0 0 2px ${avatar.color}40` : 'none',
                          }}
                        >
                          {!avatar.src && avatar.name[0].toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={avatar.name}
                        primaryTypographyProps={{
                          fontSize: '0.82rem',
                          fontWeight: avatar.selected ? 600 : 400,
                          color: '#343a40',
                        }}
                      />
                      {avatar.selected && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: avatar.color,
                          }}
                        />
                      )}
                    </ListItemButton>
                  ))
                ) : (
                  <Typography sx={{ py: 3, textAlign: 'center', color: '#adb5bd', fontSize: '0.82rem' }}>
                    {tGeneral('noDoctorsFound')}
                  </Typography>
                )}
              </List>
            </Popover>
          </>
        )}
      </Box>
    </Box>
  );
};

export default HorizontalAvatarList;